import { IWebsiteSetting } from "../../customTypings/common/WebsiteSetting";
import { Field, Form, Formik, type FormikHelpers } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditFormActions, EditFormSaveCancelButtons } from "../../components/Forms";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import * as Yup from "yup";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { websiteSettingService } from "../../services";
import { arePayloadsEqual, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, isUnchangedUpdateResponse, NO_CHANGES_TOAST_MESSAGE, shouldShowFormFieldError, showToastError, showToastSuccess, showToastUnchanged, appRoutes } from "../../utils";

/** Keep in sync with FroomWasteTracker.Data.Config.SessionTimeoutPolicy (MinHours / MaxHours). */
const SESSION_TIMEOUT_MIN = 1;
const SESSION_TIMEOUT_MAX = 168;

const websiteSettingsValidationSchema = Yup.object({
    domain: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .max(100, "Domain must be at most 100 characters.")
        .required("Domain is required."),
    invitationExpiryDays: Yup.number()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined ? NaN : value
        )
        .typeError("Enter a valid number of days.")
        .integer("Invitation expiry days must be a whole number.")
        .min(0, "Invitation expiry days must be 0 or greater.")
        .required("Invitation expiry days is required."),
    sessionTimeoutHours: Yup.mixed<number | "">().test(
        "sessionRange",
        `Session idle timeout must be between ${SESSION_TIMEOUT_MIN} and ${SESSION_TIMEOUT_MAX} hours inclusive, or left blank to use the system default.`,
        function (value) {
            if (value === "" || value === null || value === undefined) return true;
            const n = typeof value === "number" ? value : Number(value);
            if (Number.isNaN(n)) {
                return this.createError({
                    message: "Enter a whole number of hours, or leave blank.",
                });
            }
            if (!Number.isInteger(n)) {
                return this.createError({
                    message: "Session idle timeout must be a whole number of hours, or left blank.",
                });
            }
            if (n < SESSION_TIMEOUT_MIN || n > SESSION_TIMEOUT_MAX) {
                return this.createError({
                    message: `Session idle timeout must be between ${SESSION_TIMEOUT_MIN} and ${SESSION_TIMEOUT_MAX} hours inclusive, or left blank to use configuration defaults.`,
                });
            }
            return true;
        }
    ),
});

type FormShape = {
    id: string;
    domain: string;
    invitationExpiryDays: number;
    sessionTimeoutHours: number | "";
    isDeleted: boolean;
};

type ValidatedFieldName = "domain" | "invitationExpiryDays" | "sessionTimeoutHours";

function buildUpdatePayload(values: FormShape): IWebsiteSetting {
    const id = typeof values.id === "string" ? values.id.trim() : "";
    return {
        id,
        domain: typeof values.domain === "string" ? values.domain.trim() : values.domain,
        invitationExpiryDays: Number(values.invitationExpiryDays),
        sessionTimeoutHours:
            values.sessionTimeoutHours === "" || values.sessionTimeoutHours === undefined
                ? null
                : Number(values.sessionTimeoutHours),
    };
}

function buildUpdatePayloadFromRow(row: IWebsiteSetting): IWebsiteSetting {
    const st = row.sessionTimeoutHours;
    return buildUpdatePayload({
        id: row.id || "",
        domain: row.domain || "",
        invitationExpiryDays: row.invitationExpiryDays ?? 0,
        sessionTimeoutHours: st != null && st !== undefined ? st : "",
        isDeleted: false,
    });
}

const WebsiteSettingsForm = () => {
    const navigate = useNavigate();
    const [lastChangedAtUtc, setLastChangedAtUtc] = useState<string | null>(null);
    const [lastChangedByDisplayName, setLastChangedByDisplayName] = useState<string | null>(null);
    const [formData, setFormData] = useState<FormShape>({
        id: "",
        domain: "",
        invitationExpiryDays: 0,
        sessionTimeoutHours: "",
        isDeleted: false,
    });

    const [submitting, setSubmitting] = useState(false);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<IWebsiteSetting | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const formAutoFocusRef = useFormAutoFocus(!submitting, [formData.id]);

    useEffect(() => {
        if (!submitting) {
            websiteSettingService.getAll().then((res: IWebsiteSetting[]) => {
                if (res.length) {
                    const row = res[0];
                    const st = row.sessionTimeoutHours;
                    setFormData({
                        id: row.id || "",
                        domain: row.domain || "",
                        invitationExpiryDays: row.invitationExpiryDays ?? 0,
                        sessionTimeoutHours: st != null && st !== undefined ? st : "",
                        isDeleted: false,
                    });
                    savedUpdatePayloadRef.current = buildUpdatePayloadFromRow(row);
                    const id = row.id?.trim();
                    if (id) {
                        websiteSettingService
                            .get(id)
                            .then((detail) => {
                                setLastChangedAtUtc(detail.lastChangedAtUtc ?? null);
                                setLastChangedByDisplayName(detail.lastChangedByDisplayName ?? null);
                            })
                            .catch(() => {
                                setLastChangedAtUtc(null);
                                setLastChangedByDisplayName(null);
                            });
                    } else {
                        setLastChangedAtUtc(null);
                        setLastChangedByDisplayName(null);
                    }
                }
            });
        }
    }, [submitting]);

    const handleSubmit = async (values: FormShape, { resetForm }: FormikHelpers<FormShape>) => {
        saveOutcomeRef.current = null;
        setLiveValidationAfterSubmit(true);
        setSubmitting(true);
        try {
            const id = typeof values.id === "string" ? values.id.trim() : "";
            if (!id) {
                saveOutcomeRef.current = "failure";
                showToastError(
                    "Website settings could not be loaded. Contact your administrator."
                );
                return;
            }

            const data = buildUpdatePayload(values);

            if (arePayloadsEqual(data, savedUpdatePayloadRef.current)) {
                setLiveValidationAfterSubmit(false);
                saveOutcomeRef.current = "unchanged";
                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                if (leaveAfterSaveRef.current) return;
                return;
            }

            const result = await websiteSettingService.edit(data);
            if (isUnchangedUpdateResponse(result)) {
                setLiveValidationAfterSubmit(false);
                saveOutcomeRef.current = "unchanged";
                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                if (leaveAfterSaveRef.current) return;
                return;
            }

            try {
                const detail = await websiteSettingService.get(id);
                setLastChangedAtUtc(detail.lastChangedAtUtc ?? null);
                setLastChangedByDisplayName(detail.lastChangedByDisplayName ?? null);
            } catch {
                /* optional metadata */
            }

            savedUpdatePayloadRef.current = data;
            saveOutcomeRef.current = "success";
            showToastSuccess("Website settings have been saved successfully.");
            setLiveValidationAfterSubmit(false);
            resetForm({ values });
            if (leaveAfterSaveRef.current) return;
        } catch {
            saveOutcomeRef.current = "failure";
            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    const validationSchema = useMemo(() => websiteSettingsValidationSchema, []);

    if (!submitting) {
        return (
            <>
                {lastChangedAtUtc && (
                    <div className="text-muted small mb-3">
                        <span className="text-nowrap me-1">Last changed:</span>
                        {new Date(lastChangedAtUtc).toLocaleString(undefined, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                        {lastChangedByDisplayName != null && lastChangedByDisplayName !== "" && (
                            <span className="ms-1">by {lastChangedByDisplayName}</span>
                        )}
                    </div>
                )}
                <Formik
                enableReinitialize={true}
                initialValues={{
                    id: formData.id ? formData.id : "",
                    domain: formData.domain ? formData.domain : "",
                    invitationExpiryDays: formData.invitationExpiryDays ?? 0,
                    sessionTimeoutHours: formData.sessionTimeoutHours === "" ? "" : formData.sessionTimeoutHours,
                    isDeleted: false,
                }}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange={true}
                validateOnBlur={false}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, submitCount, values, handleSubmit: formikSubmit, validateForm, setTouched, isSubmitting }) => {
                    // Existing persisted row: show field validation as the user edits (no first-submit gate).
                    // New record (no id): defer messages until first Save or after an API attempt (liveValidationAfterSubmit).
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: ValidatedFieldName) =>
                        shouldShowFormFieldError(
                            errors,
                            field,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit,
                            touched
                        );
                    const form = (
                    <Form
                        ref={formAutoFocusRef}
                        noValidate
                        onSubmit={formikSubmit}
                    >
                        <div className="mb-3">
                            <label htmlFor="domain" className="form-label">
                                Domain <RequiredFieldMarker />
                            </label>
                            <Field
                                type="text"
                                className={`form-control${showErr("domain") ? " is-invalid" : ""}`}
                                id="domain"
                                name="domain"
                                placeholder="Enter domain"
                                aria-required="true"
                                aria-invalid={showErr("domain")}
                            />
                            {showErr("domain") && (
                                <div className="invalid-feedback d-block">{errors.domain}</div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label htmlFor="invitationExpiryDays" className="form-label">
                                Invitation Expiry Days <RequiredFieldMarker />
                            </label>
                            <Field
                                type="number"
                                className={`form-control w-5em${showErr("invitationExpiryDays") ? " is-invalid" : ""}`}
                                id="invitationExpiryDays"
                                name="invitationExpiryDays"
                                placeholder="Enter invitation expiry days"
                                aria-required="true"
                                aria-invalid={showErr("invitationExpiryDays")}
                            />
                            {showErr("invitationExpiryDays") && (
                                <div className="invalid-feedback d-block">{errors.invitationExpiryDays}</div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label htmlFor="sessionTimeoutHours" className="form-label">
                                Session idle timeout (hours)
                            </label>
                            <Field
                                type="number"
                                className={`form-control w-max-16rem${showErr("sessionTimeoutHours") ? " is-invalid" : ""}`}
                                id="sessionTimeoutHours"
                                name="sessionTimeoutHours"
                                placeholder="Leave blank for default (12h)"
                                min={SESSION_TIMEOUT_MIN}
                                max={SESSION_TIMEOUT_MAX}
                                aria-invalid={showErr("sessionTimeoutHours")}
                            />
                            {showErr("sessionTimeoutHours") && (
                                <div className="invalid-feedback d-block">{errors.sessionTimeoutHours}</div>
                            )}
                            <div className="form-text text-muted">
                                Optional. If left blank, the system default (12 hours) applies. Changes apply after the application is restarted.
                            </div>
                        </div>

                        <EditFormActions>
                            <EditFormSaveCancelButtons
                                isSubmitting={isSubmitting || submitting}
                                enableSaveBeforeLeave={hasPersistedRecord}
                                leaveAfterSaveRef={leaveAfterSaveRef}
                                saveOutcomeRef={saveOutcomeRef}
                                onCancel={() => navigate(appRoutes.systemSetup)}
                            />
                        </EditFormActions>
                    </Form>
                    );

                    if (!hasPersistedRecord) {
                        return form;
                    }

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            isNewRecord={false}
                            enableSaveBeforeLeave
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>
            </>
        );
    }
    return (
        <div className="spinner-border text-success" role="status">
            <span className="sr-only">Submitting...</span>
        </div>
    );
};

export default WebsiteSettingsForm;
